import { useEffect, useRef } from "react";

import { useShadowRootElements } from "@backstage/plugin-techdocs-react";

/**
 * Injects a `<style>` element into the TechDocs shadow root and keeps its
 * content in sync with the `css` argument.
 *
 * A `data-addon` attribute set to `addonId` is applied to the injected
 * element so it can be identified and cleaned up independently of other
 * addon styles. The element is removed automatically when the component
 * using this hook unmounts.
 *
 * @param addonId - A unique identifier for the addon that owns this style
 *   block. Used as the `data-addon` attribute value on the `<style>` element.
 * @param css - The raw CSS string to inject. Updated on every render where
 *   the value changes.
 */
export function useShadowStyle(addonId: string, css: string): void {
    const [body] = useShadowRootElements(["body"]);
    const styleRef = useRef<HTMLStyleElement | null>(null);

    useEffect(() => {
        if (!body) return undefined;
        const root = body.getRootNode() as ShadowRoot;

        const style = document.createElement("style");
        style.setAttribute("data-addon", addonId);
        root.appendChild(style);
        styleRef.current = style;

        return () => {
            style.remove();
            styleRef.current = null;
        };
    }, [body, addonId]);

    useEffect(() => {
        if (!styleRef.current) return;
        styleRef.current.textContent = css;
    }, [css, body]);
}
